import { ogImageMeta, hubOgKey, bestOgKey } from "@/lib/og-images";
import { createFileRoute, Link } from "@tanstack/react-router";
import { fetchBestList } from "@/lib/library-best.functions";
import { BestListPage, bestJsonLd } from "@/components/best-list";
import { breadcrumbJsonLd } from "@/components/library-hub";
import { SiteFooter } from "@/components/site-footer";

export const Route = createFileRoute("/library/best/$slug")({
  loader: async ({ params }) => {
    try {
      const data = await fetchBestList({ data: { slug: params.slug } });
      return { data };
    } catch {
      return { data: null };
    }
  },
  head: ({ params, loaderData }) => {
    const url = `https://vibing.inc/library/best/${params.slug}`;
    const data = loaderData?.data ?? null;
    if (!data) {
      return {
        meta: [
          { title: "Round-up not found — The Vibeary" },
          { name: "robots", content: "noindex" },
        ],
        links: [{ rel: "canonical", href: url }],
      };
    }
    const { list, gems } = data;
    const title = `${list.metaTitle} | The Vibeary`;
    return {
      meta: [
        { title },
        { name: "description", content: list.metaDescription },
        { property: "og:title", content: list.metaTitle },
        { property: "og:description", content: list.metaDescription },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        ...ogImageMeta(bestOgKey(list.slug), `Share card: ${list.title}`),
        { name: "twitter:card", content: "summary_large_image" },
        ...(gems.length < 5 ? [{ name: "robots", content: "noindex,follow" }] : []),
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        { type: "application/ld+json", children: bestJsonLd(list, gems, url) },
        {
          type: "application/ld+json",
          children: breadcrumbJsonLd([
            { name: "Home", item: "https://vibing.inc/" },
            { name: "The Vibeary", item: "https://vibing.inc/library" },
            { name: "Round-ups", item: "https://vibing.inc/library/best" },
            { name: list.title, item: url },
          ]),
        },
      ],
    };
  },
  component: BestRoundUp,
});

function BestRoundUp() {
  const { data } = Route.useLoaderData();

  if (!data) {
    return (
      <div className="min-h-dvh bg-background text-foreground">
        <main id="main" className="mx-auto max-w-3xl px-6 py-24 text-center">
          <h1 className="font-display text-3xl">Round-up not found</h1>
          <Link to="/library" className="mt-4 inline-block text-muted-foreground underline">
            Back to the library
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return <BestListPage list={data.list} gems={data.gems} />;
}
